import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

// API's
import { retrieveManagedPositions } from "@/Common/api/location";
import { getUsersPayPlanAssignment } from "@PayPlans/api/users/queries";

// Helpers
import { hasViewAccess } from "@/PayPlans/helpers/Access";

// Subscriptions
import { getAssignmentsSubscriptions, getOnCreateAssignment, getOnRemoveUser } from "./subscriptions";

// Contexts
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";
import useQueryParam from "@/Common/hooks/Router/useQueryParam";
import { useLocationId } from "@Common/hooks/Location/useLocationId";

// Layouts
import Assignments from "@/PayPlans/Layouts/Assignments";

// Custom Components
import IndividualContent from "@/PayPlans/components/Assignments/Contents/Individual";

// Types
import { IUser } from "@Common/interfaces/user.ts";

/********************************************* HELPERS ************************************************/
const groupUsersByPosition = (position: string, users: IUser[] = []) => {
	const matchUsers = users.filter((user) => user.position === position);
	return matchUsers.map((user) => {
		return {
			_id: user._id,
			title: user.fullName,
			position,
		};
	});
};

export default function Individual() {
	const locationId = useLocationId();
	const { isPresentAccess } = useAuthorization();
	const { clearCache } = useCacheCleaner();
	const { addSubscription, clearSubscriptionContext } = useSubscriptions();

	// States
	const [selected, setSelected] = useState<{ _id: string; position: string; title: string } | null>(null);

	// Position detection
	const queryParam = useQueryParam();
	const [userPosition, userId, userName] = queryParam.getAll("selected");

	/********************************************* QUERIES ************************************************/
	const { data: managedPositions, isLoading: isLoadingPositions } = useQuery({
		queryKey: ["payPlanAssignmentManagedPositions", locationId],
		queryFn: async () => {
			const positions = await retrieveManagedPositions(locationId);
			return {
				positions,
				retrievablePositions: positions?.filter((position) => hasViewAccess(position, isPresentAccess)),
			};
		},
		staleTime: Infinity,
		enabled: !!locationId,
	});

	const { data: tree, isLoading } = useQuery({
		queryKey: ["payPlanAssignmentUsers", locationId],
		queryFn: async () => {
			const { positions, retrievablePositions } = managedPositions || {};
			const users = await getUsersPayPlanAssignment(locationId, retrievablePositions, "ACTIVE");

			return positions?.map((position) => {
				const list = groupUsersByPosition(position, users);
				return {
					_id: position,
					title: position,
					bold: true,
					disabled: !list.length,
					list,
				};
			});
		},
		staleTime: Infinity,
		enabled: !!managedPositions,
	});

	/********************************************* HANDLERS ************************************************/
	const handleSelection = (element: { _id: string; position: string; title: string }) => {
		if (element) {
			addSubscription(["payPlanAssignment", element._id], getAssignmentsSubscriptions());
			setSelected(element);
		}
	};

	useEffect(() => {
		if (!location) {
			return;
		}

		if (!!userPosition && !!userId && !!userName) {
			addSubscription(
				["payPlanAssignment", userId],
				[
					{
						context: "onCreatePayPlanAssignment",
						query: getOnCreateAssignment(),
					},
					{
						context: "onRemoveUserPayPlanAssignment",
						query: getOnRemoveUser(),
					},
				]
			);
			setSelected({
				_id: userId,
				position: userPosition,
				title: userName,
			});
		}

		return () => {
			setSelected(null);
		};
	}, [addSubscription, location, userId, userName, userPosition]);

	useEffect(() => {
		return () => {
			clearCache(["retrieveSalesPayPlanAssignment", "usersByCurrentPayPlan", "payPlanAssignmentCurrentAssignment"]);
		};
	}, [clearCache]);

	useEffect(() => {
		return () => {
			clearCache(["assignSelectedPlanButton"]);
		};
	}, []);

	useEffect(() => {
		return () => {
			clearSubscriptionContext();
		};
	}, [clearSubscriptionContext]);

	return (
		<Assignments
			tree={tree}
			selected={selected}
			loading={isLoading || isLoadingPositions}
			isLocationAvailable={Boolean(location)}
			isUserPosition={Boolean(userPosition)}
			onActionSelected={handleSelection}
		>
			<IndividualContent selectedItem={selected} locationId={locationId} />
		</Assignments>
	);
}
